import { Route } from '@angular/router';
import { permissionGuard } from './guards/permission.guard';
import { unauthenticatedGuard } from './guards/unauthenticated.guard';
import { CanExitGuard } from './guards/guards/deactivate.guard';
import { offerDetailsGuard } from './guards/offer-details.guard';
import { coBorrowerSignupGuard } from './guards/coborrower-signup.guard';

export const appRoutes: Route[] = [
  {
    path: '',
    canActivate: [permissionGuard],
    children: [],
    pathMatch: 'full',
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./features/identity/login/login.component').then(
        (c) => c.LoginComponent
      ),
  },
  {
    path: 'signup',
    loadComponent: () =>
      import('./features/identity/signup/signup.component').then(
        (c) => c.SignupComponent
      ),
  },
  {
    path: 'forgotpassword',
    loadComponent: () =>
      import('./features/identity/forgotpassword/forgotpassword.component').then(
        (c) => c.ForgotPasswordComponent
      ),
  },
  {
    path: 'checkyouremail',
    loadComponent: () =>
      import('./features/identity/checkyouremail/checkyouremail.component').then(
        (c) => c.CheckYourEmailComponent
      ),
  },
  {
    path: 'resetpassword',
    loadComponent: () =>
      import('./features/identity/resetpassword/resetpassword.component').then(
        (c) => c.ResetPasswordComponent
      ),
  },
  {
    path: 'dashboard',
    data: { breadcrumb: 'Dashboard' },
    canActivate: [permissionGuard],
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then(
        (c) => c.DashboardComponent
      ),
  },
  {
    // Public self‑registration entry – only for unauthenticated users
    path: 'intake/:providerCode',
    canActivate: [unauthenticatedGuard],
    canDeactivate: [CanExitGuard],
    loadComponent: () =>
      import('./features/stepper-ignyte/stepper-ignyte.component').then(
        (c) => c.StepperIgnyteComponent
      ),
  },
  {
        path: 'offer-details/:invitationCode',
        data: { breadcrumb: 'Application Form' },        
        canActivate: [offerDetailsGuard],
        canDeactivate: [CanExitGuard],
        loadComponent: () =>
          import('./features/stepper-ignyte/stepper-ignyte.component').then(
            (c) => c.StepperIgnyteComponent
          ),
      },
  {
    path: 'students/coborrower/:invitationCode',
    canActivate: [coBorrowerSignupGuard],
    loadComponent: () =>
      import('./features/identity/signup/signup.component').then(
        (c) => c.SignupComponent
      ),
  },
  {
    path: 'student/application/:invitationCode',
    data: {
      breadcrumb: 'Application Details',
      permissions: ['view_application'],
    },
    canActivate: [permissionGuard],
    canDeactivate: [CanExitGuard],
    loadComponent: () =>
      import(
        './features/students/manage-applications/manage-applications.component'
      ).then((c) => c.ManageApplicationsComponent),
  },
  {
    path: 'student/applications-selection',
    data: {
      breadcrumb: 'Application Selection'
    },
    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './features/students/application-selection/application-selection.component'
          ).then((c) => c.ApplicationSelectionComponent),
      },
      {
        path: 'application/:invitationCode',
        data: {
          breadcrumb: 'Application Details',
          permissions: ['view_application'],
        },
        canActivate: [permissionGuard],
        canDeactivate: [CanExitGuard],
        loadComponent: () =>
          import(
            './features/students/manage-applications/manage-applications.component'
          ).then((c) => c.ManageApplicationsComponent),
      }
    ]
  },
  {
    path: 'students',
    data: {
      breadcrumb: 'Application Management',
      permissions: ['view_application'],
    },
    canActivate: [permissionGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'student-management',
      },
      {
        path: 'applications',
        data: { breadcrumb: 'Applications' },
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/students/student-management/applications-list/applications-list.component'
              ).then((c) => c.ApplicationsListComponent),
          },
          {
            path: 'details/:invitationCode',
            data: {
              breadcrumb: 'Application Details',
              permissions: ['view_school_application_list'],
            },
            canActivate: [permissionGuard],
            canDeactivate: [CanExitGuard],
            loadComponent: () =>
              import(
                './features/students/manage-applications/school-application/school-application.component'
              ).then((c) => c.SchoolApplicationComponent),
          },
          {
            path: ':invitationCode',
            data: {
              breadcrumb: 'Application Details',
              permissions: ['view_application'],
            },
            canActivate: [permissionGuard],
            canDeactivate: [CanExitGuard],
            loadComponent: () =>
              import(
                './features/students/manage-applications/manage-applications.component'
              ).then((c) => c.ManageApplicationsComponent),
          },
        ],
      },
      {
        path: 'analytics-dashboard',
        data: { breadcrumb: 'Analytics Dashboard' },
        loadComponent: () =>
          import(
            './features/analytics-dashboard/analytics-dashboard.component'
          ).then((c) => c.AnalyticsDashboardComponent),
      },
      {
        path: 'invitations',
        data: {
          breadcrumb: 'Invitations',
          permissions: ['view_invitation_list'],
        },
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (c) => c.DashboardComponent
          ),
      },
      {
        path: 'student-management',
        data: { breadcrumb: 'Invitations' },
        loadComponent: () =>
          import(
            './features/students/student-management/student-management.component'
          ).then((c) => c.StudentManagementComponent),
      },
    ],
  },
  {
    path: 'refunds',
    data: {
      breadcrumb: 'Refund Requests',
      permissions: ['view_application'],
    },
    loadComponent: () =>
      import(
        './features/students/refunds/refund-requests/refund-requests.component'
      ).then((c) => c.RefundRequestsComponent),
  },
  {
    path: 'tasks',
    canActivate: [permissionGuard],
    data: {
      breadcrumb: 'Task Management',
      permissions: ['view_operations_application_list'],
    },
    loadComponent: () =>
      import(
        './features/task-management/task-management.component'
      ).then((c) => c.TaskManagementComponent),
  },
  {
    path: 'school-management',
    data: {
      breadcrumb: 'Provider Management',
      permissions: ['view_operations_application_list'],
    },
    canActivate: [permissionGuard],
    children: [
      {
        path: '',
        redirectTo: 'provider-management',
        pathMatch: 'full',
      },
      {
        path: 'provider-management',
        loadComponent: () =>
          import(
            './features/provider-management/provider-management.component'
          ).then((c) => c.ProviderManagementComponent),
      },
      {
        path: 'provider-management/:schoolId',
        data: { breadcrumb: '' },
        loadComponent: () =>
          import(
            './features/provider-management/provider-management.component'
          ).then((c) => c.ProviderManagementComponent),
      },
    ],
  },
  {
    path: 'offer/:invitationCode',
    data: { breadcrumb: 'Application Form' },
    loadComponent: () =>
      import('./features/offer/offer.component').then((c) => c.OfferComponent),
  },
  {
    path: 'otp',
    loadComponent: () =>
      import('./features/identity/otp/otp.component').then(
        (c) => c.OtpComponent
      ),
  },
  {
    path: 'error',
    data: { hideSidebarAndHeader: true },
    loadComponent: () =>
      import(
        '../../../../shared/invessio-library/src/lib/components/error-screen/error-screen.component'
      ).then((m) => m.ErrorScreenComponent),
  },
  {
    path: '**',
    redirectTo: 'error',
  },
];
